/**
 * i18n key + variant-pool source of truth for the in-session toast pipeline.
 *
 * Shared between mobile and web so adding a new milestone/combo variant
 * means editing ONE map — clients only need their own render adapter
 * (i18next `t()` on mobile, `<Trans>` on web).
 *
 * Key shape:
 *   - `studyGroups.leaderboard.toast.milestone.t${tier}.${variant}`
 *   - `studyGroups.leaderboard.toast.combo.c${count}.${variant}`
 *   - `studyGroups.leaderboard.toast.streakActive.s${streak}`  (no variant pool)
 *   - `studyGroups.leaderboard.earlyExit.podium1|podium2|podium3|movedUp|dropped|still`
 */

import type { ComboTier, MilestoneTier, StreakTier, ToastTrigger } from './toast-rules';
import { COMBO_TIERS, MILESTONE_TIERS } from './toast-rules';

/** The title keys per milestone tier — the variant pool, 1-indexed
 *  (`v1`, `v2`, ...). Drift between this map and the actual translation
 *  entries shows up at runtime as a key miss (sonner renders the raw key),
 *  not silent. */
const MILESTONE_VARIANT_KEYS: Readonly<Record<MilestoneTier, readonly string[]>> = {
  25: [
    'studyGroups.leaderboard.toast.milestone.t25.v1',
    'studyGroups.leaderboard.toast.milestone.t25.v2',
    'studyGroups.leaderboard.toast.milestone.t25.v3',
  ],
  50: [
    'studyGroups.leaderboard.toast.milestone.t50.v1',
    'studyGroups.leaderboard.toast.milestone.t50.v2',
  ],
  75: [
    'studyGroups.leaderboard.toast.milestone.t75.v1',
    'studyGroups.leaderboard.toast.milestone.t75.v2',
  ],
  100: [
    'studyGroups.leaderboard.toast.milestone.t100.v1',
    'studyGroups.leaderboard.toast.milestone.t100.v2',
    'studyGroups.leaderboard.toast.milestone.t100.v3',
  ],
  125: [
    'studyGroups.leaderboard.toast.milestone.t125.v1',
    'studyGroups.leaderboard.toast.milestone.t125.v2',
  ],
  150: [
    'studyGroups.leaderboard.toast.milestone.t150.v1',
    'studyGroups.leaderboard.toast.milestone.t150.v2',
  ],
  200: [
    'studyGroups.leaderboard.toast.milestone.t200.v1',
    'studyGroups.leaderboard.toast.milestone.t200.v2',
  ],
  250: ['studyGroups.leaderboard.toast.milestone.t250.v1'],
};

/** The title keys per combo tier. Same 1-indexed convention. */
const COMBO_VARIANT_KEYS: Readonly<Record<ComboTier, readonly string[]>> = {
  5: [
    'studyGroups.leaderboard.toast.combo.c5.v1',
    'studyGroups.leaderboard.toast.combo.c5.v2',
    'studyGroups.leaderboard.toast.combo.c5.v3',
  ],
  10: [
    'studyGroups.leaderboard.toast.combo.c10.v1',
    'studyGroups.leaderboard.toast.combo.c10.v2',
  ],
  15: [
    'studyGroups.leaderboard.toast.combo.c15.v1',
    'studyGroups.leaderboard.toast.combo.c15.v2',
  ],
  20: [
    'studyGroups.leaderboard.toast.combo.c20.v1',
    'studyGroups.leaderboard.toast.combo.c20.v2',
  ],
};

/** The title key per streak tier (no variant pool). */
const STREAK_ACTIVE_KEY: Readonly<Record<StreakTier, string>> = {
  2: 'studyGroups.leaderboard.toast.streakActive.s2',
  3: 'studyGroups.leaderboard.toast.streakActive.s3',
  5: 'studyGroups.leaderboard.toast.streakActive.s5',
  7: 'studyGroups.leaderboard.toast.streakActive.s7',
  14: 'studyGroups.leaderboard.toast.streakActive.s14',
  30: 'studyGroups.leaderboard.toast.streakActive.s30',
};

const poolSizes = <K extends number>(
  keys: Readonly<Record<K, readonly string[]>>,
): Readonly<Record<K, number>> =>
  Object.fromEntries(
    Object.entries<readonly string[]>(keys).map(([tier, pool]) => [tier, pool.length]),
  ) as Record<K, number>;

/** Variant pool size per milestone tier. */
export const MILESTONE_VARIANT_POOL_SIZES = poolSizes(MILESTONE_VARIANT_KEYS);

/** Variant pool size per combo tier. */
export const COMBO_VARIANT_POOL_SIZES = poolSizes(COMBO_VARIANT_KEYS);

// Compile-time guards — if MILESTONE_TIERS / COMBO_TIERS ever drift from
// the pool maps above, TypeScript flags it via the Record<Tier, …> shape.
type _AssertMilestonePool = (typeof MILESTONE_TIERS)[number] extends MilestoneTier
  ? true
  : never;
type _AssertComboPool = (typeof COMBO_TIERS)[number] extends ComboTier ? true : never;
const _milestoneAssert: _AssertMilestonePool = true;
const _comboAssert: _AssertComboPool = true;
void _milestoneAssert;
void _comboAssert;

/**
 * Pick a 1-based variant suffix `v${n}` within `poolSize`. The random
 * call is the only non-pure thing in this module; callers wanting a
 * deterministic test can pass their own variant by skipping this and
 * computing the i18n key themselves. */
export function pickVariant(poolSize: number): string {
  const idx = Math.floor(Math.random() * poolSize) + 1;
  return `v${idx}`;
}

/** One title key from a variant pool, uniformly at random. */
function pickKey(pool: readonly string[]): string {
  return pool[Math.floor(Math.random() * pool.length)] ?? pool[0] ?? '';
}

/** Platform-agnostic badge icon token — the UI layers map these to their
 *  own lucide import (`Coins`/`Zap`/`Flame` from lucide-react on web,
 *  lucide-react-native on mobile). Shared code must stay icon-lib-free. */
export type ToastBadgeIcon = 'coins' | 'zap' | 'flame';

export type ToastI18nKey = {
  /** TITLE i18n key — the flavor line, e.g.
   *  `studyGroups.leaderboard.toast.milestone.t25.v1` = "Warming up 🔥". */
  key: string;
  /** Interpolation values for the DESCRIPTION line (early-exit rank). */
  values?: Record<string, unknown>;
  /** DESCRIPTION i18n key — the fixed "so what?" line under the title. The
   *  early-exit family's carries the rank; the in-session families each have
   *  one fixed encouragement line shared by every tier/variant. */
  descriptionKey?: string;
  /** The QUANTITY, rendered in the DS Toast's trailing badge — never in
   *  the prose (design-system house rule). */
  badgeLabel: string;
  badgeIcon?: ToastBadgeIcon;
};

/**
 * Map a toast trigger to its DS-Toast payload: title key (variant pool),
 * fixed description key, and the quantity badge. The renderer consumes
 * the result — adding a new trigger kind adds one branch here, and a new
 * variant only bumps the pool size above. */
export function pickToastI18nKey(trigger: ToastTrigger): ToastI18nKey {
  switch (trigger.kind) {
    case 'milestone': {
      return {
        key: pickKey(MILESTONE_VARIANT_KEYS[trigger.tier]),
        descriptionKey: 'studyGroups.leaderboard.toast.milestoneDescription',
        badgeLabel: `+${trigger.tier} MP`,
        badgeIcon: 'coins',
      };
    }
    case 'combo': {
      return {
        key: pickKey(COMBO_VARIANT_KEYS[trigger.count]),
        descriptionKey: 'studyGroups.leaderboard.toast.comboDescription',
        badgeLabel: String(trigger.count),
        badgeIcon: 'zap',
      };
    }
    case 'streakActive': {
      return {
        key: STREAK_ACTIVE_KEY[trigger.streak],
        descriptionKey: 'studyGroups.leaderboard.toast.streakActiveDescription',
        badgeLabel: `×${trigger.multiplier}`,
        badgeIcon: 'flame',
      };
    }
  }
}

/** Pick the rank-driven early-exit toast payload. The fixed title lives
 *  at `earlyExit.title` ("Wooah!"), the rank line is the DESCRIPTION,
 *  and the MP earned goes in the badge (no badge icon on purpose — the
 *  rank emoji already carries the celebration).
 *
 * Six description variants — three podium positions (1/2/3) and three
 * off-podium directions (movedUp / dropped / still). `prevRank === null`
 * (no pre-snapshot captured) degrades off-podium to `still` so the toast
 * never picks a wrong direction. */
export function pickEarlyExitI18nKey(args: {
  /** Display MP earned this session — `+{value} MP` in the badge. */
  value: number;
  currentRank: number;
  /** Pre-session rank from the snapshot, or null when missing. */
  prevRank: number | null;
}): ToastI18nKey {
  const { value, currentRank, prevRank } = args;
  const base = {
    key: 'studyGroups.leaderboard.earlyExit.title',
    badgeLabel: `+${value} MP`,
  };
  if (currentRank === 1) {
    return { ...base, descriptionKey: 'studyGroups.leaderboard.earlyExit.podium1' };
  }
  if (currentRank === 2) {
    return { ...base, descriptionKey: 'studyGroups.leaderboard.earlyExit.podium2' };
  }
  if (currentRank === 3) {
    return { ...base, descriptionKey: 'studyGroups.leaderboard.earlyExit.podium3' };
  }
  const values: Record<string, unknown> = { rank: currentRank };
  if (prevRank !== null && prevRank > currentRank) {
    return {
      ...base,
      descriptionKey: 'studyGroups.leaderboard.earlyExit.movedUp',
      values,
    };
  }
  if (prevRank !== null && prevRank < currentRank) {
    return {
      ...base,
      descriptionKey: 'studyGroups.leaderboard.earlyExit.dropped',
      values,
    };
  }
  return { ...base, descriptionKey: 'studyGroups.leaderboard.earlyExit.still', values };
}
